import { canStartSignIn } from '@cascade/core/auth';
import { Fragment, useEffect, useMemo, useSyncExternalStore } from 'react';
import { AuthenticationContext } from './Authentication.context.ts';
import { hasSession } from './Authentication.helpers.ts';
import type {
  TAuthenticationContextValue,
  TAuthenticationProviderProps,
} from './Authentication.types.ts';

export const AuthenticationProvider = ({
  auth,
  signInCallback,
  children,
}: TAuthenticationProviderProps) => {
  const state = useSyncExternalStore(auth.subscribe, () => auth.state);
  const isAuthenticated = hasSession(state);
  const sessionKey = hasSession(state) ? state.user.id : 'anonymous';

  const value = useMemo<TAuthenticationContextValue>(
    () => ({
      ...state,
      isAuthenticated,
      canStartSignIn: canStartSignIn(state),
      refresh: auth.refresh,
      signIn: auth.signIn,
      signInWithProvider: auth.signInWithProvider,
      signInAsDemo: auth.signInAsDemo,
      signOut: auth.signOut,
      expire: auth.expire,
    }),
    [state, isAuthenticated, auth],
  );

  useEffect(() => {
    auth.restore(signInCallback).catch(() => undefined);
  }, [auth, signInCallback]);

  return (
    <AuthenticationContext.Provider value={value}>
      <Fragment key={sessionKey}>{children}</Fragment>
    </AuthenticationContext.Provider>
  );
};
